import React, { useContext } from 'react';
import { Box, Typography, Badge, Button, CircularProgress } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { DTB, DTBPackageItem } from 'views/App/Portal/DTB/_types/dtb';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { AppContext } from 'views/App/context';

interface SelectedDtbsTabProps {
  selectedDtbs: DTB[];
  packageDtbs: DTBPackageItem[];
  setSelectedDtbs: React.Dispatch<React.SetStateAction<DTB[]>>;
  onAddDtbs: () => void;
  addDisabled: boolean;
  isAddingDtbs?: boolean;
}

type DTBWithRowType = DTB & { _rowType: string };

const SelectedDtbsTab: React.FC<SelectedDtbsTabProps> = ({
  selectedDtbs,
  packageDtbs,
  setSelectedDtbs,
  onAddDtbs,
  addDisabled,
  isAddingDtbs,
}) => {
  const { isLexipolStaff } = useContext(AppContext);
  // Only show selected DTBs in the grid
  const allDtbs: DTBWithRowType[] = selectedDtbs.map((dtb) => ({ ...dtb, _rowType: 'selected' }));

  // Use IDs for selection model
  const selectedIds = selectedDtbs.map((dtb) => dtb.id);

  // When selection changes, update selectedDtbs in parent
  const handleSelectionChange = (ids: string[]) => {
    // Find the DTB objects for the selected IDs
    const newSelected = allDtbs.filter((dtb) => ids.includes(dtb.id));
    setSelectedDtbs(newSelected);
  };

  const columns: ColumnDef = {
    name: {
      header: t('dtbs.packages.detail.grid.name'),
      flex: 1.5,
      renderer: (params: GridCellParams) => <Typography variant='h6'>{params.row.name}</Typography>,
    },
    topic: {
      header: t('dtbs.packages.detail.grid.topic', 'Topic'),
      flex: 1,
      renderer: (params: GridCellParams) => <Typography>{params.row.topic}</Typography>,
    },
    manual: {
      header: t('dtbs.packages.detail.grid.manualName'),
      flex: 1,
      renderer: (params: GridCellParams) => (
        <Typography>{params.row.manual?.name || t('dtbs.packages.noManual')}</Typography>
      ),
    },
    package: {
      header: t('dtbs.packages.detail.grid.package', 'Package'),
      flex: 1,
      renderer: (params: GridCellParams) => <Typography>{'-'}</Typography>,
    },
    ...(isLexipolStaff
      ? {
          jurisdiction: {
            header: t('dtbs.packages.detail.grid.jurisdiction', 'Jurisdiction'),
            flex: 1,
            renderer: (params: GridCellParams) => <Typography>{params.row.jurisdiction?.name}</Typography>,
          },
        }
      : {}),
    status: {
      header: t('dtbs.packages.detail.grid.status'),
      flex: 0.7,
      renderer: (params: GridCellParams) => {
        const status = params.row.status?.toUpperCase() || 'DRAFT';
        const color = status === 'APPROVED' ? 'success' : 'neutral';
        return (
          <Badge
            variant='inline'
            badgeContent={t(`dtbs.status.${status}`, status)}
            color={color}
            sx={{ textTransform: 'uppercase' }}
          />
        );
      },
    },
  };

  if (allDtbs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>{t('dtbs.packages.detail.noDtbsInPackage')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant='contained'
          onClick={onAddDtbs}
          disabled={addDisabled}
          endIcon={isAddingDtbs ? <CircularProgress size={16} /> : undefined}
        >
          {t('dtbs.packages.detail.searchInLibrary.addDtbs')}
        </Button>
      </Box>
      <GridSection
        columns={columns}
        data={allDtbs}
        query={{ isLoading: false, isError: false, data: { pages: [{ count: allDtbs.length }] } }}
        gridProps={{
          rowHeight: 64,
          columnHeaderHeight: 40,
          checkboxSelection: true,
          rowSelectionModel: selectedIds,
          onRowSelectionModelChange: (ids: any) => handleSelectionChange(ids),
        }}
      />
    </Box>
  );
};

export default SelectedDtbsTab;
